import { AsideItemComponent } from '@qima-labs/app/pages/main/shared/components/aside/shared/components/aside-item/aside-item.component';
import { MENU_ITEMS_COMMON } from '@qima-labs/app/pages/main/shared/components/aside/shared/constants/aside-menus.constants';
import { IAsideItem } from '@qima-labs/app/pages/main/shared/components/aside/shared/models/interfaces/aside-item.interface';
import { AsideService } from '@qima-labs/app/pages/main/shared/components/aside/shared/services/aside.service';
import { UserService } from '@qima-labs/app/shared/services/user-services/user.service';
import { OverlayModule } from '@qsp-ui-library/components/overlay/overlay.module';
import { TrackByUuidDirective } from '@qsp-ui-library/directives/track-by-uuid.directive';
import { AppUserSettingService } from '@qsp-ui-library/services/user-services/app-user-setting/app-user-setting.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { EQimaIconName, EQimaPosition, qimaPixelsToEm, QimaResponsiveService, QimaTooltipDirectiveModule } from '@qima/ngx-qima';

@UntilDestroy()
@Component({
  selector: 'app-aside',
  templateUrl: './aside.component.html',
  styleUrls: ['./aside.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('expanded', style({ width: `${qimaPixelsToEm(240)}em` })),
      state('collapsed', style({ width: `${qimaPixelsToEm(76)}em` })),
      transition('* => *', [animate('200ms ease-in-out')]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, OverlayModule, AsideItemComponent, SlicePipe, TranslateModule, QimaTooltipDirectiveModule, TrackByUuidDirective],
})
export class AsideComponent implements OnInit {
  /**
   * @description
   * Show or hide the aside menu of the app
   * @type {boolean}
   * @default false
   */
  public isNetworkOnly = this._appUserSettingService.getEnableUseNetwork() && !this._appUserSettingService.getEnableFoodCertification();
  public isSubMenuVisible: boolean = false;
  public isAsideMenuVisible: boolean = false;
  public isAsideMoreVisible: boolean = false;
  public asideItemCheckedIndex: number = -1;
  public subMenuItemCheckedIndex: number = -1;
  public isAsideCollapsed: boolean = false;
  public tooltipPosition: EQimaPosition = EQimaPosition.RIGHT;
  public isMobile: boolean = false;
  public menuItems: IAsideItem[] = [];

  public constructor(
    private readonly _appUserSettingService: AppUserSettingService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _asideService: AsideService,
    private readonly _router: Router,
    private readonly _userService: UserService,
    private readonly _qimaResponsiveService: QimaResponsiveService
  ) {}

  //
  public ngOnInit(): void {
    this._initData();
    this._watch();
  }

  //
  public onAsideItemClick(index: Readonly<number>, item: IAsideItem, subMenuItems?: IAsideItem[]): void {
    if (item.subItems?.length) {
      this.isSubMenuVisible = true;
      this.subMenuItemCheckedIndex = index;
      this._asideService.updateAsideItemCheckedIndex(index);
    } else {
      // TODO: remind for more menu
      // this.isSubMenuVisible = false;
      //
      if (subMenuItems) {
        index = -1;
        this._asideService.updateAsideItemCheckedIndex(index);
      }
      // TODO: remind for more menu
      // this._asideService.updateAsideItemCheckedIndex(index);
      // this._asideService.updateAsideMenuVisible(false);
      //
      // if (!item.path) {
      //   return;
      // }

      void this._router.navigate([item.path]);
    }
  }

  public onOverlayClick(): void {
    this._asideService.updateAsideMenuVisible(false);
  }

  //
  public onBookNowClick(): void {
    this._asideService.updateAsideMenuVisible(!this.isAsideMenuVisible);
  }

  private _watch(): void {
    this._asideService.asideMenuVisible$.pipe(untilDestroyed(this)).subscribe((isVisible: boolean): void => {
      this.isAsideMenuVisible = isVisible;
    });
    //
    this._asideService.asideItemCheckedIndex$.pipe(untilDestroyed(this)).subscribe((index: number): void => {
      this.asideItemCheckedIndex = index;
      this._changeDetectorRef.markForCheck();
    });
    //
    this._asideService.isAsideCollapsed$.pipe(untilDestroyed(this)).subscribe((isAsideCollapsed: boolean): void => {
      this.isAsideCollapsed = isAsideCollapsed;
      this._changeDetectorRef.markForCheck();
    });
    //
    this._asideService.collapseMenuClick$.pipe(untilDestroyed(this)).subscribe((): void => {
      this.isAsideCollapsed = !this.isAsideCollapsed;
      this._asideService.updateIsAsideCollapsed(this.isAsideCollapsed);
      this._changeDetectorRef.markForCheck();
    });

    this._router.events.pipe(untilDestroyed(this)).subscribe((event): void => {
      if (event instanceof NavigationEnd) {
        this._updateAsideItemCheckedIndex(event.url);
        this._changeDetectorRef.markForCheck();
      }
    });

    this._qimaResponsiveService
      .isMobile$()
      .pipe(untilDestroyed(this))
      .subscribe((isMobile: boolean): void => {
        this.isMobile = isMobile;
        this._asideService.updateIsAsideCollapsed(!isMobile);
        this.isAsideMoreVisible = false;

        if (isMobile) {
          this.tooltipPosition = EQimaPosition.TOP;
          this._updateMoreMenuItem();
        } else {
          this.tooltipPosition = EQimaPosition.RIGHT;
          this._setMenuItems();
        }

        this._changeDetectorRef.markForCheck();
      });
  }

  private _initData(): void {
    this._setMenuItems();
    this._changeDetectorRef.markForCheck();
  }

  private _setMenuItems(): void {
    this.menuItems = [];
    this._userService
      .getUserPermissionsArray$()
      .pipe(untilDestroyed(this))
      .subscribe((permissionsResponse: string[]): void => {
        const permissionArray = permissionsResponse;

        this.menuItems = MENU_ITEMS_COMMON.map((item: IAsideItem): IAsideItem => {
          item.visible = item.visible ? permissionArray.includes(item.permissionKey) : false;

          return item;
        });
        this._updateAsideItemCheckedIndex(this._router.routerState.snapshot.url);
        this._changeDetectorRef.markForCheck();
      });
  }

  private _updateAsideItemCheckedIndex(url: string): void {
    if (!url) {
      return;
    }

    this.asideItemCheckedIndex = -1;

    this.menuItems.forEach((item: IAsideItem, index: number): void => {
      if (item.matchPath) {
        const isQimaLabsPathMatch = url.startsWith(item.matchPath);

        if (isQimaLabsPathMatch) {
          this.asideItemCheckedIndex = index;
        }
      }
    });

    this._changeDetectorRef.markForCheck();
  }

  private _updateMoreMenuItem(): void {
    if (!this.isMobile) {
      return;
    }

    const hasMoreMenu = this.menuItems.some((item): boolean => item.id === 'moreMenu');

    if (!hasMoreMenu && this.menuItems.length > 5) {
      this.menuItems.splice(4, 0, {
        id: 'moreMenu',
        label: 'COMMON.MORE',
        iconName: EQimaIconName.MENU,
        matchPath: '',
        path: '',
        tooltip: '',
        visible: true,
        permissionKey: '',
      });
    }
  }
}
